import React, { FC } from 'react'
import Head from 'next/head'
import {
  useAnalytics,
  useCatalogConfig,
  useSetting,
  useSettingBvf,
  useSettingDefault
} from '@ui/hooks'
import { store_type } from '@libs/common/models/constant'
import { useRouter } from 'next/router'
import {
  // getImagesLink,
  // imagePath,
  iconPathBvf,
  imagePath
} from '@ui/helpers'
import {
  FbpScript,
  GtagScript,
  PinterestScript,
  SnapchatScript,
  TiktokPixelScript
} from '@ui/analytics'
import getConfig from 'next/config'

const sellpageImage = (page: S3Types.page_setting | null) => {
  if (!page) return ''
  const defaultId = page?.variants?.default_variant_id
  return page?.variants?.items?.[defaultId]?.logo ?? page?.gallery?.[0] ?? ''
}

const gtmId_GA4 = getConfig().publicRuntimeConfig.gtmId || ''
const pixelForm = getConfig().publicRuntimeConfig.pixelForm || ''
const enable_speedCurve = getConfig().publicRuntimeConfig.enable_speedCurve || ''
const cdnBasePath = getConfig().publicRuntimeConfig.cdnBasePath || ''
const cdnBasePathBvf = getConfig().publicRuntimeConfig.cdnBasePathBvf || ''

const MyHead: FC = () => {
  const router = useRouter()
  const slug = router.query.subpath?.toString()
  const collectionPath = router.query.collection_path?.toString()
  const settingDefault = useSettingDefault()
  const [storeSetting] = useSetting('store')
  const [sellpageSetting] = useSetting('sellpage')
  const [configCollection] = useCatalogConfig()
  const settingBvf = useSettingBvf()
  const analytics = useAnalytics()
  const isMinistore = storeSetting?.general.type === store_type.MINISTORE
  const collectionTitle = configCollection.label
    ? `${storeSetting?.general?.name} | Best ${
        configCollection.label.toLowerCase() == 'bestsellers' ? 'bra' : configCollection.label
      } collections for women`
    : (storeSetting?.general?.title || storeSetting?.general?.domain?.split('.')?.[0]) ?? ''
  // const storeName = storeSetting?.general?.name
  const sellpageTitle = `${storeSetting?.general?.name} | ${
    sellpageSetting?.settings?.general.title ?? ''
  }`
  const title = slug
    ? sellpageTitle
    : collectionPath
    ? collectionTitle
    : router.asPath.includes('help-center')
    ? `${storeSetting?.general?.name} | Help Center`
    : router.asPath.includes('about-us')
    ? `${storeSetting?.general?.name} | About us`
    : settingBvf?.page_title
  const description =
    isMinistore && !slug
      ? storeSetting?.general?.meta?.description
      : sellpageSetting?.settings?.general?.description
  const image =
    isMinistore && !slug
      ? iconPathBvf(settingBvf?.meta_image || 'img-wingbras-min.jpg')
      : imagePath(sellpageImage(sellpageSetting))
  const url = `${storeSetting?.general?.domain}${slug ? `/p/${slug}` : ''}`
  const isAdsForm = router.pathname?.includes('senior-model-hiring-external')
  const fbpPixelIds = isAdsForm ? [`${pixelForm}`] : analytics?.pixel_ids ?? []
  const tiktokPixelIds = analytics?.tiktok_ids ?? []
  const snapchatPixelIds = analytics?.snapchat_ids ?? []
  // const pinterestIds = analytics?.pinterest_ids ?? []
  const gaIdsAdmin = (settingDefault?.setting?.settings.general?.ga_ids ?? '').split(',')
  const gaIds = [...(storeSetting?.analytics?.google_analytic_ids ?? []), ...(gaIdsAdmin ?? [])]

  const isThankyouPage = router.pathname?.startsWith('/thankyou')

  const canonicalUrl = (
    `https://${storeSetting?.general?.domain}` +
    (router.asPath === '/' ? '' : router.query.static_slug?.toString() ? '/static' : router.asPath)
  ).split('?')[0]
  return (
    <Head>
      <title>{title || 'Storefront'}</title>
      <link rel="shortcut icon" href="/favicon.ico" type="image/x-icon" />
      <link rel="canonical" href={canonicalUrl} />
      <link rel="preconnect" href={`${cdnBasePath}`} />
      <link rel="preconnect" href={`${cdnBasePathBvf}`} />
      <link rel="preconnect" href="https://fonts.gstatic.com" />
      <link rel="preconnect" href="https://cdnjs.cloudflare.com" />
      <link rel="dns-prefetch" href={`${cdnBasePath}`} />
      <link rel="dns-prefetch" href={`${cdnBasePathBvf}`} />
      <link rel="preload" as="image" href={image}></link>
      <meta httpEquiv="x-dns-prefetch-control" content="on"></meta>
      <meta
        key="viewport"
        name="viewport"
        content="width=device-width, initial-scale=1, user-scalable=no"
      ></meta>
      <meta key="mobile-web-app-capable" name="mobile-web-app-capable" content="yes"></meta>
      <meta
        key="apple-mobile-web-app-capable"
        name="apple-mobile-web-app-capable"
        content="false"
      ></meta>
      <meta
        key="apple-mobile-web-app-title"
        name="apple-mobile-web-app-title"
        content={title || 'storefront'}
      ></meta>
      <meta key="description" name="description" content={description || 'storefront'}></meta>
      <meta key="theme-color" name="theme-color" content="#1B1B1B"></meta>
      <meta property="og:type" content={slug ? 'product' : 'website'}></meta>
      <meta property="og:title" content={title || 'storefront'}></meta>
      <meta property="og:site_name" content={title || 'storefront'}></meta>
      <meta property="og:description" content={description || 'storefront'}></meta>
      <meta property="og:image" content={image}></meta>
      <meta property="og:url" content={url ?? ''}></meta>
      <meta
        name="google-site-verification"
        content={storeSetting?.analytics?.google_verification_id || ''}
      ></meta>
      <meta
        name="p:domain_verify"
        content={storeSetting?.analytics?.pinterest_verification_id || ''}
      ></meta>
      <meta
        name="facebook-domain-verification"
        content={storeSetting?.general.meta?.facebook_domain_verification || ''}
      ></meta>
      <SnapchatScript pixel_ids={snapchatPixelIds} />
      <PinterestScript />
      <FbpScript pixel_ids={fbpPixelIds} />
      {tiktokPixelIds.length && <TiktokPixelScript pixel_ids={tiktokPixelIds} />}

      {gaIds.length > 0 && <GtagScript gaIds={gaIds} />}
      {!isThankyouPage && <CustomScript />}
      {enable_speedCurve && (
        <script
          dangerouslySetInnerHTML={{
            __html: `LUX=(function(){var a=("undefined"!==typeof(LUX)&&"undefined"!==typeof(LUX.gaMarks)?LUX.gaMarks:[]);var d=("undefined"!==typeof(LUX)&&"undefined"!==typeof(LUX.gaMeasures)?LUX.gaMeasures:[]);var j="LUX_start";var k=window.performance;var l=("undefined"!==typeof(LUX)&&LUX.ns?LUX.ns:(Date.now?Date.now():+(new Date())));if(k&&k.timing&&k.timing.navigationStart){l=k.timing.navigationStart}function f(){if(k&&k.now){return k.now()}var o=Date.now?Date.now():+(new Date());return o-l}function b(n){if(k){if(k.mark){return k.mark(n)}else{if(k.webkitMark){return k.webkitMark(n)}}}a.push({name:n,entryType:"mark",startTime:f(),duration:0});return}function m(p,t,n){if("undefined"===typeof(t)&&h(j)){t=j}if(k){if(k.measure){if(t){if(n){return k.measure(p,t,n)}else{return k.measure(p,t)}}else{return k.measure(p)}}else{if(k.webkitMeasure){return k.webkitMeasure(p,t,n)}}}var r=0,o=f();if(t){var s=h(t);if(s){r=s.startTime}else{if(k&&k.timing&&k.timing[t]){r=k.timing[t]-k.timing.navigationStart}else{return}}}if(n){var q=h(n);if(q){o=q.startTime}else{if(k&&k.timing&&k.timing[n]){o=k.timing[n]-k.timing.navigationStart}else{return}}}d.push({name:p,entryType:"measure",startTime:r,duration:(o-r)});return}function h(n){return c(n,g())}function c(p,o){for(i=o.length-1;i>=0;i--){var n=o[i];if(p===n.name){return n}}return undefined}function g(){if(k){if(k.getEntriesByType){return k.getEntriesByType("mark")}else{if(k.webkitGetEntriesByType){return k.webkitGetEntriesByType("mark")}}}return a}return{mark:b,measure:m,gaMarks:a,gaMeasures:d}})();LUX.ns=(Date.now?Date.now():+(new Date()));LUX.ac=[];LUX.cmd=function(a){LUX.ac.push(a)};LUX.init=function(){LUX.cmd(["init"])};LUX.send=function(){LUX.cmd(["send"])};LUX.addData=function(a,b){LUX.cmd(["addData",a,b])};LUX_ae=[];window.addEventListener("error",function(a){LUX_ae.push(a)});LUX_al=[];if("function"===typeof(PerformanceObserver)&&"function"===typeof(PerformanceLongTaskTiming)){var LongTaskObserver=new PerformanceObserver(function(c){var b=c.getEntries();for(var a=0;a<b.length;a++){var d=b[a];LUX_al.push(d)}});try{LongTaskObserver.observe({type:["longtask"]})}catch(e){}};</script><script src="https://cdn.speedcurve.com/js/lux.js?id=4345223413" defer crossorigin="anonymous">`
          }}
        ></script>
      )}
    </Head>
  )
}

const CustomScript = () => {
  return (
    <>
      <script
        defer
        dangerouslySetInnerHTML={{
          __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${gtmId_GA4}');`
        }}
      ></script>
    </>
  )
}

export default MyHead
